import moment from "moment";
import { RoomSelectionItem } from "../components/examSession/builder/BuilderRoomList";
import { ExamSessionViewModel } from "../model/ExamSessionViewModel";
import * as yup from "yup";
import { LocationViewModel } from "../model/LocationViewModel";
import { ExamDayViewModel } from "../model/ExamDayViewModel";
import {
  TimeSlotStructureType,
  TimeSlotStructure,
  getRoomTypeFromSelectedTimeSlot,
  TimeSlotType,
} from "../model/TimeSlotStructure";
import { ExamDayType } from "../model/ExamDayType";
import { RoomAssignmentViewModel } from "../model/RoomAssignmentViewModel";
import { Language, SwissTimeZone } from "../data/constants";
import { ExamAssignmentViewModel } from "../model/ExamAssignmentViewModel";
import { ExamTemplateWithStructureIndex } from "../model/Exam";

export class ExamSessionBuilderHelper {
  private errors: string[] = [];
  private examSession: ExamSessionViewModel;
  private schema = yup.object().shape({
    sessionKeyDE: yup.string().required("examSession.validation.nameIsRequiredInAllLanguages"),
    sessionKeyFR: yup.string().required("examSession.validation.nameIsRequiredInAllLanguages"),
    sessionKeyIT: yup.string().required("examSession.validation.nameIsRequiredInAllLanguages"),
    sessionKeyEN: yup.string().required("examSession.validation.nameIsRequiredInAllLanguages"),
    locations: yup.array().min(1, "examSession.validation.atLeastOneLocationIsRequired"),
  });
  private roomSelection: RoomSelectionItem[] = [];

  constructor(examSession: ExamSessionViewModel) {
    this.examSession = {...examSession};
    this.examSession.locations = [];
    this.examSession.examDays = [];
    this.examSession.exams = [];
    this.examSession.locations = [];
    moment.tz.setDefault("UTC");
  }

  public getErrors(): string[] {
    return this.errors;
  }

  public Build(): ExamSessionViewModel | null {
    this.Validate();
    moment.tz.setDefault();
    if (this.errors.length > 0) {
      console.error("Errors found during exam session build:", this.errors);
      return null;
    }
    return this.examSession;
  }

  public addRooms(rooms: RoomSelectionItem[]) {
    this.roomSelection = rooms;
    return this;
  }

  public addLocation(location: LocationViewModel) {
    this.examSession.locations.push(location);
    return this;
  }

  public addExamWithTypeStructure(exam: ExamTemplateWithStructureIndex) {
    this.examSession.exams.push({...exam, courseTypes: exam.availableCourseTypes});
    return this;
  }

  public addDays(days: Date[], structureType: TimeSlotStructureType, structure: TimeSlotStructure, examId: string) {
    if (days.length === 0) {
      this.errors.push("examSession.validation.atLeastOneExamDayIsRequired");
      return this;
    }
    const timeOfLastExam = Math.max(...days.map((d) => d.getTime()));
    const dateOfLastExam = this.getDateOnly(new Date(timeOfLastExam));
    const timeOfFirstExam = Math.min(...days.map((d) => d.getTime()));
    const dateOfFirstExam = this.getDateOnly(new Date(timeOfFirstExam));

    this.examSession.signupStartDate = moment(dateOfFirstExam).subtract(2, "weeks").startOf("day").hours(10).tz(SwissTimeZone, true).utc().toDate();
    this.examSession.signupEndDate = moment(dateOfLastExam).subtract(2, "weeks").endOf("day").tz(SwissTimeZone, true).utc().toDate();
    this.examSession.examsStartDate = moment(dateOfFirstExam).startOf("day").toDate();
    this.examSession.examsEndDate = moment(dateOfLastExam).startOf("day").toDate();
    this.examSession.freeCancellationEndDate = moment(dateOfFirstExam).subtract(2, "weeks").startOf("day").toDate();
    this.examSession.correctionStartDate = moment(dateOfLastExam).add(10, "days").startOf("day").toDate();
    this.examSession.correctionEndDate = moment(dateOfLastExam).add(11, "days").startOf("day").toDate();
    this.examSession.inspectionStartDate = moment(dateOfLastExam).add(20, "days").startOf("day").toDate();
    this.examSession.inspectionEndDate = moment(dateOfLastExam).add(21, "days").startOf("day").toDate();
    this.examSession.gradingConferenceDate = moment(dateOfLastExam).add(15, "days").startOf("day").toDate();
    this.examSession.examResultDate = moment(dateOfLastExam).add(16, "days").startOf("day").toDate();
    this.examSession.from = moment
      .min([
        moment(),
        moment(this.examSession.examsStartDate),
        moment(this.examSession.signupStartDate),
        moment(this.examSession.correctionStartDate),
      ])
      .toDate();
    this.examSession.to = moment
      .max([
        moment(dateOfLastExam),
        moment(this.examSession.examsEndDate),
        moment(this.examSession.signupEndDate),
        moment(this.examSession.correctionEndDate),
        moment(this.examSession.inspectionEndDate),
        moment(this.examSession.gradingConferenceDate),
        moment(this.examSession.examResultDate),
      ])
      .toDate();
    this.examSession.to = dateOfLastExam;

    const roomType = getRoomTypeFromSelectedTimeSlot(structureType) as ExamDayType;
    const roomAssignments = this.roomSelection.map((room) => {
      const languages = this.getLanguagesFromRoomSelection(room);
      if (languages.length === 0) {
        this.errors.push("examSession.validation.atLeastOneLanguageIsSelectedForEachRoom");
      }
      const timeSlots = structure[structureType];
      const examAssignments: ExamAssignmentViewModel[] = timeSlots
        .filter((t) => t.type === TimeSlotType.Exam)
        .map((timeSlot, index) => {
          return {
            examId: examId,
            timeSlotIndex: index + 1, // time slot starting at 1
          };
        });
      return {
        languages,
        roomId: room.roomId,
        isPreparationRoom: false,
        examAssignments,
        assignedUsers: [],
        assignedCorrectionExperts: [],
        id: "",
      } as RoomAssignmentViewModel;
    });
    days.forEach((day) => {
      const examDay: ExamDayViewModel = {
        date: this.getDateOnly(day),
        roomAssignments: roomAssignments!,
        locationId: this.examSession.locations[0].id, // The first location is used for all exam days
        timeSlotStructureType: structureType,
        type: roomType,
        id: "",
        availableUsers: [],
        availableCorrectionExperts: [],
      };
      this.examSession.examDays.push(examDay);
    });

    return this;
  }

  public addIsLife(isLife: boolean) {
    this.examSession.isLife = isLife;
    return this;
  }

  private Validate() {
    try {
      this.schema.validateSync({...this.examSession}, { abortEarly: false });
    } catch (err) {
      if (err && typeof err === "object" && "inner" in err && Array.isArray((err as any).inner)) {
        (err as any).inner.forEach((error: yup.ValidationError) => {
          if (!this.errors.includes(error.message)) {
            this.errors.push(error.message);
          }
        });
      } else if (err instanceof Error) {
        this.errors.push(err.message);
      }
      return null;
    }

    return this.errors.length === 0;
  }

  private getLanguagesFromRoomSelection(room: RoomSelectionItem): string[] {
    const languages: string[] = [];
    if (room.deSelected) {
      languages.push(Language.DE);
    }
    if (room.frSelected) {
      languages.push(Language.FR);
    }
    if (room.itSelected) {
      languages.push(Language.IT);
    }
    return languages;
  }

  private getDateOnly(date: Date): Date {
    return moment.utc({ year :date.getFullYear(), month :date.getMonth(), day :date.getDate()}).local().toDate();
  }
}
